import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Button } from '@mdvip/shared/components/ui/button';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemberAutoPaySummary } from '@/hooks/payments/autoPay/useMemberAutoPaySummary';
import { useAddBankAccount } from '@/hooks/payments/bankAccount/useAddBankAccount';
import { useMemberBillingProfile } from '@/hooks/payments/billing/useMemberBillingProfile';
import { useAddCreditCard } from '@/hooks/payments/creditCard/useAddCreditCard';
import { usePaymentAccountForm } from '@/hooks/payments/usePaymentAccountForm';
import type {
  AddBankAccountInput,
  AddCreditCardInput,
  BillingAddressValues,
  PaymentTab,
} from '@/lib/payments/paymentAccount.types';
import { PaymentMethodForm } from './PaymentMethodForm';
import { PaymentReviewScreen } from './PaymentReviewScreen';

interface AddPaymentAccountModalBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Initial tab; lets callers open straight to Credit Card or Bank Account. */
  defaultTab?: PaymentTab;
  /**
   * Called after a successful submit. The caller decides what happens next (close + toast,
   * return to the AutoPay flow, select the new account in the pay modal, …) — the modal does
   * not assume. Keeps it reusable across the Payment Accounts page, AutoPay, and one-time pay.
   */
  onSuccess?: () => void;
  /**
   * Called when the member presses the footer's Cancel instead of dismissing with X, Escape or the
   * overlay. Both discard what was entered, but Cancel is a deliberate step back, so a caller that
   * opened this from another modal can return there rather than dropping the member on the page
   * behind. Omit it and Cancel just closes, which is what the Payment Accounts page wants.
   */
  onCancel?: () => void;
}

// Discriminated union so a caller can't pass `fromAutopay` without `onBackToAutopay`: without
// this, `fromAutopay={true}` alone renders a back arrow that resets the form and silently no-ops
// on navigation, leaving the member stuck on a cleared modal — mirrors the onBack/backLabel
// pairing enforced on ResponsiveModalProps.
export type AddPaymentAccountModalProps = AddPaymentAccountModalBaseProps &
  (
    | {
        /**
         * Opened from AutoPay enrollment: locks AutoPay on (badged "Enrolling" rather than
         * silently, so it reads differently from an already-enrolled member) and shows a
         * desktop-only back arrow wired to `onBackToAutopay`.
         */
        fromAutopay: true;
        /** Back-arrow target on the entry screen — required together with `fromAutopay`. */
        onBackToAutopay: () => void;
      }
    | { fromAutopay?: false; onBackToAutopay?: never }
  );

/**
 * Add Payment Account modal (CNP-133). Owns the single RHF instance + screen/tab state, hosts
 * Screen 1 (entry) and Screen 2 (review), and wires the verify→create submit through the
 * stub hooks. Standalone for now; structured so AutoPay/one-time-pay can reuse it additively.
 */
export function AddPaymentAccountModal({
  open,
  onOpenChange,
  defaultTab = 'credit-card',
  fromAutopay = false,
  onBackToAutopay,
  onSuccess,
  onCancel,
}: AddPaymentAccountModalProps) {
  const { t } = useTranslation('members');
  const [screen, setScreen] = useState<'form' | 'review'>('form');
  const [activeTab, setActiveTab] = useState<PaymentTab>(defaultTab);
  // Default closed. Visibility/requirement derive from this + savedAddress so they react
  // correctly once the (async) profile loads — never seeded from a not-yet-loaded address.
  const [billingFormOpen, setBillingFormOpen] = useState(false);

  const profile = useMemberBillingProfile().data;
  const savedAddress = profile?.savedAddress ?? null;
  const billingRequired = !savedAddress || billingFormOpen;

  // Gates the Review transition below — advancing before this resolves risks submitting a stale
  // (default) autoPay value for an already-enrolled member, and the review screen itself needs
  // autoPayAmount/autoPayPlan from the same query.
  const { data: autoPaySummary, isLoading: isAutoPaySummaryLoading } = useMemberAutoPaySummary();

  const form = usePaymentAccountForm({ activeTab, billingRequired });

  // Return to a clean entry form: clears all fields (incl. PAN/CVV from memory) and collapses the
  // billing sub-form. Used on dismiss, on successful submit, and on each open.
  const resetModal = useCallback(() => {
    form.reset();
    setScreen('form');
    setBillingFormOpen(false);
  }, [form]);

  // The page mounts a single modal and only flips `open`/`defaultTab` (and closes it
  // programmatically after submit). Resetting on every open — not just on dismiss — re-syncs the
  // launch tab and guarantees no stale screen or retained data leaks into the next open.
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
      resetModal();
    }
  }, [open, defaultTab, resetModal]);

  const handleSubmitSuccess = () => {
    onSuccess?.();
    resetModal(); // clear card data from memory immediately after a successful add (PCI)
  };
  const cardMutation = useAddCreditCard({ onSuccess: handleSubmitSuccess });
  const bankMutation = useAddBankAccount({ onSuccess: handleSubmitSuccess });
  const isSubmitting = cardMutation.isPending || bankMutation.isPending;

  // The billing address that will be submitted: the entered sub-form when required/open,
  // otherwise the member's saved address. Subscribed via useWatch, not read with getValues: a
  // render-time getValues isn't reactive, so the React Compiler caches the object it returned, and
  // the reset on open replaces that object — review and submit would then see the empty original.
  const enteredBilling = useWatch({ control: form.control, name: 'billing' });
  const effectiveBilling: BillingAddressValues = billingRequired ? enteredBilling : (savedAddress ?? enteredBilling);

  const handleOpenChange = (next: boolean) => {
    // Dismiss discards everything (AC: X / overlay discards data).
    if (!next) resetModal();
    onOpenChange(next);
  };

  /**
   * Footer Cancel. Discards the same as a dismiss, but hands navigation to `onCancel` when the
   * caller supplied one, so a modal that opened this can take the member back instead of closing
   * outright. Mirrors `handleBackToAutopay`, which likewise resets without calling `onOpenChange`.
   */
  const handleCancel = () => {
    resetModal();
    if (onCancel) onCancel();
    else onOpenChange(false);
  };

  // Closing the "add new" billing form discards what was entered (matches the design's Discard).
  const handleToggleBillingForm = () => {
    if (billingFormOpen) form.resetField('billing');
    setBillingFormOpen((isOpen) => !isOpen);
  };

  const handleConfirm = () => {
    const values = form.getValues();
    if (activeTab === 'credit-card') {
      const input: AddCreditCardInput = { ...values.card, billing: effectiveBilling };
      cardMutation.submit(input);
    } else {
      const input: AddBankAccountInput = { ...values.bank, billing: effectiveBilling };
      bankMutation.submit(input);
    }
  };

  // Validate the active tab + billing against the form schema, then advance. Lives here (not in
  // PaymentMethodForm) so the Review action can sit in the sticky footer, outside the scrolling body.
  const handleReview = form.handleSubmit(() => setScreen('review'));

  // Leaving to the hosting AutoPay flow discards entered data too (same PCI hygiene as dismiss),
  // but doesn't call the caller's onOpenChange — the parent's own back-handler owns navigation.
  const handleBackToAutopay = () => {
    resetModal();
    onBackToAutopay?.();
  };

  // Header back arrow (desktop-only) exists solely for the fromAutopay entry screen — the design
  // gives the review screen no header back affordance at all; "Make changes" in the footer covers it.
  const showBackArrow = screen === 'form' && fromAutopay;

  // Typed to match ResponsiveModal's onBack/backLabel discriminated union — a plain ternary
  // object loses that discrimination when spread (TS widens both props to optional instead).
  const backProps: { onBack: () => void; backLabel: string } | { onBack?: never; backLabel?: never } = showBackArrow
    ? { onBack: handleBackToAutopay, backLabel: t('payments.addAccount.backToAutopay') }
    : {};

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange} className="sm:max-w-2xl" {...backProps}>
      <FormProvider {...form}>
        {/* Extra left padding (desktop only) clears the back arrow whenever it's shown. */}
        <ResponsiveModalHeader className={showBackArrow ? 'sm:pl-14' : undefined}>
          <ResponsiveModalTitle>
            {screen === 'form' ? t('payments.addAccount.title') : t('payments.addAccount.reviewScreen.title')}
          </ResponsiveModalTitle>
          {screen === 'review' && (
            <ResponsiveModalDescription>{t('payments.addAccount.reviewScreen.description')}</ResponsiveModalDescription>
          )}
        </ResponsiveModalHeader>

        <ResponsiveModalBody>
          {screen === 'form' ? (
            <PaymentMethodForm
              activeTab={activeTab}
              onTabChange={setActiveTab}
              autoPayEnrolled={autoPaySummary?.autoPayEnrolled ?? false}
              fromAutopay={fromAutopay}
              savedAddress={savedAddress}
              billingFormOpen={billingFormOpen}
              onToggleBillingForm={handleToggleBillingForm}
            />
          ) : (
            <PaymentReviewScreen
              activeTab={activeTab}
              autoPayAmount={autoPaySummary?.autoPayAmount ?? null}
              autoPayPlan={autoPaySummary?.autoPayPlan ?? null}
              billing={effectiveBilling}
            />
          )}
        </ResponsiveModalBody>

        <ResponsiveModalFooter
          note={
            <>
              <Icon name="lock" weight="solid" size="xs" /> {t('payments.addAccount.secureNote')}
            </>
          }
        >
          {screen === 'form' ? (
            <>
              <Button type="button" variant="outline" onClick={handleCancel}>
                {t('payments.addAccount.cancel')}
              </Button>
              <Button type="button" onClick={handleReview} disabled={isAutoPaySummaryLoading}>
                {t('payments.addAccount.review')}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => setScreen('form')}>
                <Icon name="pencil" size="sm" /> {t('payments.addAccount.reviewScreen.makeChanges')}
              </Button>
              <Button type="button" onClick={handleConfirm} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Icon name="spinner" weight="solid" size="sm" className="animate-spin" />{' '}
                    {t('payments.addAccount.reviewScreen.submitting')}
                  </>
                ) : (
                  <>
                    <Icon name="check" weight="solid" size="sm" /> {t('payments.addAccount.reviewScreen.confirmSubmit')}
                  </>
                )}
              </Button>
            </>
          )}
        </ResponsiveModalFooter>
      </FormProvider>
    </ResponsiveModal>
  );
}
